import { Fragment, useMemo, useState } from "react";
import { downloadCsvMatrix, type CsvCell } from "@/lib/csv";
import { formatFullDate, formatRelativeTime } from "@/lib/format";
import {
  codeStatusLabel,
  computeBatchStats,
  downloadBatchDetailCsv,
  downloadBatchStats,
  summarizeBatchDetail,
} from "@/lib/codePoolExport";
import type { CodeExportLog, LicenseCode } from "@/pages/admin/tools/hooks/useLicenseTools";

// 导出类型的中文名与配色，让「未核销清单 / 批次对账包 / 核销清单」一眼可分。
const KIND_META: Record<string, { label: string; tone: string }> = {
  remainder: { label: "未核销清单", tone: "bg-primary-100 text-primary-700" },
  reconcile: { label: "批次对账包", tone: "bg-accent-100 text-accent-700" },
  "reconcile-all": { label: "全部批次对账包", tone: "bg-accent-100 text-accent-800" },
  redeem: { label: "核销清单", tone: "bg-secondary-100 text-secondary-900" },
};

type RangeKey = "all" | "today" | "7d" | "30d";

const RANGES: { key: RangeKey; label: string }[] = [
  { key: "all", label: "全部时间" },
  { key: "today", label: "今天" },
  { key: "7d", label: "近 7 天" },
  { key: "30d", label: "近 30 天" },
];

const PREVIEW_COUNT = 8;
const DETAIL_PREVIEW = 60;

function kindLabel(kind: string): string {
  return KIND_META[kind]?.label ?? kind;
}

function kindTone(kind: string): string {
  return KIND_META[kind]?.tone ?? "bg-background-200 text-foreground-700";
}

const STATUS_TONE: Record<string, string> = {
  used: "bg-primary-100 text-primary-700",
  unused: "bg-secondary-100 text-secondary-900",
  disabled: "bg-background-200 text-foreground-600",
};

function withinRange(iso: string, range: RangeKey): boolean {
  if (range === "all") return true;
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return false;
  if (range === "today") {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return time >= start.getTime();
  }
  const days = range === "7d" ? 7 : 30;
  return Date.now() - time <= days * 86400000;
}

interface ExportAuditTrailProps {
  logs: CodeExportLog[];
  codes?: LicenseCode[];
}

// 导出留痕 · 可追溯独立列表：把「谁在什么时候导出了哪个批次、多少条」集中展示，
// 支持按导出人 / 时间范围 / 导出类型翻查；点某条留痕可下钻展开该次导出涉及的批次明细。
export default function ExportAuditTrail({ logs, codes = [] }: ExportAuditTrailProps) {
  const [exporter, setExporter] = useState<string>("all");
  const [range, setRange] = useState<RangeKey>("all");
  const [kind, setKind] = useState<string>("all");
  const [expanded, setExpanded] = useState(false);
  const [openLogId, setOpenLogId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const showFeedback = (msg: string) => {
    setFeedback(msg);
    window.setTimeout(() => setFeedback(null), 3000);
  };

  const exporters = useMemo(() => {
    const set = new Set<string>();
    logs.forEach((log) => set.add(log.exported_by_name || "未知"));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "zh-CN"));
  }, [logs]);

  const kinds = useMemo(() => {
    const set = new Set<string>();
    logs.forEach((log) => set.add(log.kind));
    return Array.from(set);
  }, [logs]);

  // 全部批次的汇总（供「全部批次对账包」类留痕下钻展示每个批次）。
  const allBatches = useMemo(() => computeBatchStats(codes), [codes]);

  const filtered = useMemo(
    () =>
      logs.filter((log) => {
        if (exporter !== "all" && (log.exported_by_name || "未知") !== exporter) return false;
        if (kind !== "all" && log.kind !== kind) return false;
        return withinRange(log.created_at, range);
      }),
    [logs, exporter, kind, range],
  );

  const totalCount = useMemo(
    () => filtered.reduce((sum, log) => sum + (log.exported_count || 0), 0),
    [filtered],
  );

  const hasFilter = exporter !== "all" || range !== "all" || kind !== "all";
  const visible = expanded ? filtered : filtered.slice(0, PREVIEW_COUNT);
  const rangeLabel = RANGES.find((item) => item.key === range)?.label ?? "全部时间";

  const resetFilters = () => {
    setExporter("all");
    setRange("all");
    setKind("all");
  };

  const downloadTrail = () => {
    if (filtered.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    const summary: CsvCell[][] = [
      ["导出留痕清单"],
      [`导出时间：${formatFullDate(new Date().toISOString())}`],
      [
        `筛选条件：导出人 ${exporter === "all" ? "全部" : exporter} · 时间 ${rangeLabel} · 类型 ${
          kind === "all" ? "全部" : kindLabel(kind)
        }`,
      ],
      [`记录数：${filtered.length} · 累计导出条数：${totalCount}`],
      [],
    ];
    const header = ["导出时间", "导出人", "导出类型", "批次", "导出条数"];
    const rows: CsvCell[][] = filtered.map((log) => [
      formatFullDate(log.created_at),
      log.exported_by_name || "未知",
      kindLabel(log.kind),
      log.batch_id,
      log.exported_count,
    ]);
    downloadCsvMatrix(`导出留痕_${stamp}.csv`, [...summary, header, ...rows]);
  };

  // 单批次留痕的下钻明细：取该批次在激活码池里的逐条记录。
  const batchDetail = useMemo(() => {
    if (openLogId === null) return null;
    const log = filtered.find((item) => item.id === openLogId);
    if (!log) return null;
    if (log.kind === "reconcile-all" || log.batch_id === "全部批次") return null;
    return summarizeBatchDetail(codes, log.batch_id);
  }, [openLogId, filtered, codes]);

  const isAllBatchesOpen = useMemo(() => {
    if (openLogId === null) return false;
    const log = filtered.find((item) => item.id === openLogId);
    return !!log && (log.kind === "reconcile-all" || log.batch_id === "全部批次");
  }, [openLogId, filtered]);

  const toggleLog = (id: number) => {
    setOpenLogId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
            <i className="ri-history-line" />
          </span>
          <div>
            <h2 className="font-heading text-base font-semibold text-foreground-950">导出留痕 · 可追溯列表</h2>
            <p className="text-xs text-foreground-500">
              记录每次清单导出的操作人、时间、批次与条数，点任意一条可下钻查看该次导出涉及的具体批次。
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-[11px] font-medium text-foreground-700">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-list-check-2" />
            </span>
            {filtered.length} 条 · 累计 {totalCount} 个
          </span>
          <button
            type="button"
            disabled={filtered.length === 0}
            onClick={downloadTrail}
            title="把当前筛选出的留痕记录导出成一份追溯清单"
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-download-2-line" />
            </span>
            导出当前留痕
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
        <label className="flex items-center gap-2 text-xs text-foreground-600">
          <span className="flex h-3.5 w-3.5 items-center justify-center text-foreground-400">
            <i className="ri-user-line" />
          </span>
          <select
            value={exporter}
            onChange={(event) => setExporter(event.target.value)}
            className="h-9 min-w-[140px] cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-800 outline-none transition-colors hover:bg-background-100 focus:border-primary-400"
          >
            <option value="all">全部导出人</option>
            {exporters.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-xs text-foreground-600">
          <span className="flex h-3.5 w-3.5 items-center justify-center text-foreground-400">
            <i className="ri-price-tag-3-line" />
          </span>
          <select
            value={kind}
            onChange={(event) => setKind(event.target.value)}
            className="h-9 min-w-[150px] cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-800 outline-none transition-colors hover:bg-background-100 focus:border-primary-400"
          >
            <option value="all">全部类型</option>
            {kinds.map((item) => (
              <option key={item} value={item}>
                {kindLabel(item)}
              </option>
            ))}
          </select>
        </label>

        <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
          {RANGES.map((item) => {
            const active = range === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setRange(item.key)}
                aria-pressed={active}
                className={`inline-flex cursor-pointer items-center whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                  active ? "bg-secondary-500 text-background-50" : "text-foreground-600 hover:bg-background-50"
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {hasFilter && (
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium text-foreground-600 transition-colors hover:bg-background-100"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-close-circle-line" />
            </span>
            清除筛选
          </button>
        )}
      </div>

      {logs.length === 0 ? (
        <div className="mt-4 flex flex-col items-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100 py-10 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-history-line text-xl" />
          </span>
          <p className="text-sm font-medium text-foreground-800">还没有导出留痕</p>
          <p className="text-xs text-foreground-500">在上方下载清单或对账包后，这里会记下每一次导出的操作人与时间。</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-4 flex flex-col items-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100 py-10 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-filter-off-line text-xl" />
          </span>
          <p className="text-sm font-medium text-foreground-800">当前筛选条件下没有记录</p>
          <p className="text-xs text-foreground-500">试试放宽导出人或时间范围。</p>
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[800px] text-sm">
            <thead>
              <tr className="bg-background-100 text-left text-xs font-medium text-foreground-600">
                <th className="w-10 px-3 py-3"></th>
                <th className="px-4 py-3">导出时间</th>
                <th className="px-4 py-3">导出人</th>
                <th className="px-4 py-3">导出类型</th>
                <th className="px-4 py-3">批次</th>
                <th className="px-4 py-3 text-right">导出条数</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((log, index) => {
                const isOpen = openLogId === log.id;
                const hasDetail = log.kind === "reconcile-all" || log.batch_id === "全部批次"
                  ? allBatches.length > 0
                  : codes.some((row) => (row.batch_id || "(无批次)") === log.batch_id);
                return (
                  <Fragment key={log.id ?? `${log.batch_id}-${log.created_at}-${index}`}>
                    <tr className={`border-t border-background-200 ${isOpen ? "bg-background-100" : ""}`}>
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          disabled={!hasDetail}
                          onClick={() => toggleLog(log.id)}
                          title={hasDetail ? "展开本次导出涉及的批次明细" : "该批次暂无可用明细"}
                          aria-expanded={isOpen}
                          className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-200 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <i className={isOpen ? "ri-subtract-line" : "ri-add-line"} />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-foreground-900">{formatFullDate(log.created_at)}</p>
                        <p className="text-[11px] text-foreground-400">{formatRelativeTime(log.created_at)}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-foreground-800">{log.exported_by_name || "未知"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium ${kindTone(log.kind)}`}>
                          {kindLabel(log.kind)}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground-700">{log.batch_id}</td>
                      <td className="px-4 py-3 text-right text-xs font-medium text-foreground-900">{log.exported_count}</td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-background-200 bg-background-100/70">
                        <td colSpan={6} className="px-4 py-4">
                          {feedback && (
                            <div className="mb-2 flex items-center gap-1.5 rounded-md bg-primary-50 px-2.5 py-1.5 text-[11px] font-medium text-primary-700">
                              <span className="flex h-3.5 w-3.5 items-center justify-center">
                                <i className="ri-checkbox-circle-line" />
                              </span>
                              {feedback}
                            </div>
                          )}
                          {isAllBatchesOpen ? (
                            <div className="flex flex-col gap-2">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-[11px] font-medium text-foreground-600">
                                  本次导出涉及全部 {allBatches.length} 个批次
                                </p>
                                <button
                                  type="button"
                                  disabled={allBatches.length === 0}
                                  onClick={() => {
                                    const count = downloadBatchStats(allBatches);
                                    showFeedback(`已导出全部批次明细，共 ${count} 个批次`);
                                  }}
                                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                                    <i className="ri-download-2-line" />
                                  </span>
                                  导出全部批次明细
                                </button>
                              </div>
                              <div className="flex flex-col gap-1.5">
                                {allBatches.map((stat) => (
                                  <div
                                    key={stat.batchId}
                                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2"
                                  >
                                    <span className="flex min-w-0 flex-col">
                                      <span className="font-mono text-xs text-foreground-900">{stat.batchId}</span>
                                      <span className="text-[11px] text-foreground-500">{stat.productName || "通用"}</span>
                                    </span>
                                    <span className="flex flex-wrap items-center gap-1.5">
                                      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-900">
                                        未核销 {stat.unused}
                                      </span>
                                      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                                        已核销 {stat.used}
                                      </span>
                                      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-200 px-2 py-0.5 text-[11px] font-medium text-foreground-700">
                                        已停用 {stat.disabled}
                                      </span>
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : batchDetail ? (
                            <div className="flex flex-col gap-2">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-[11px] font-medium text-foreground-600">
                                  批次 <span className="font-mono text-foreground-900">{batchDetail.batchId}</span> 明细
                                  {batchDetail.productName ? ` · ${batchDetail.productName}` : ""}
                                </p>
                                <span className="flex flex-wrap items-center gap-1.5">
                                  <span className="inline-flex items-center whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-900">
                                    未核销 {batchDetail.unused}
                                  </span>
                                  <span className="inline-flex items-center whitespace-nowrap rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                                    已核销 {batchDetail.used}
                                  </span>
                                  <span className="inline-flex items-center whitespace-nowrap rounded-full bg-background-200 px-2 py-0.5 text-[11px] font-medium text-foreground-700">
                                    已停用 {batchDetail.disabled}
                                  </span>
                                  <button
                                    type="button"
                                    disabled={batchDetail.items.length === 0}
                                    onClick={() => {
                                      const count = downloadBatchDetailCsv(batchDetail);
                                      showFeedback(`已导出该批次明细 ${count} 条`);
                                    }}
                                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                                      <i className="ri-download-2-line" />
                                    </span>
                                    导出该批次明细
                                  </button>
                                </span>
                              </div>
                              {batchDetail.items.length === 0 ? (
                                <p className="text-xs text-foreground-500">该批次暂无可展示的激活码明细。</p>
                              ) : (
                                <div className="overflow-hidden rounded-md border border-background-200 bg-background-50">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="bg-background-100 text-left text-[11px] font-medium text-foreground-600">
                                        <th className="px-3 py-2">激活码</th>
                                        <th className="px-3 py-2">状态</th>
                                        <th className="px-3 py-2">核销时间</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {batchDetail.items.slice(0, DETAIL_PREVIEW).map((item) => (
                                        <tr key={item.code} className="border-t border-background-200">
                                          <td className="px-3 py-2 font-mono text-foreground-800">{item.code}</td>
                                          <td className="px-3 py-2">
                                            <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_TONE[item.status] ?? "bg-background-200 text-foreground-600"}`}>
                                              {codeStatusLabel(item.status)}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-foreground-500">
                                            {item.usedAt ? formatFullDate(item.usedAt) : "—"}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {batchDetail.items.length > DETAIL_PREVIEW && (
                                    <p className="border-t border-background-200 bg-background-100 px-3 py-2 text-[11px] text-foreground-500">
                                      仅展示前 {DETAIL_PREVIEW} 条，还有 {batchDetail.items.length - DETAIL_PREVIEW} 条未列出。
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-xs text-foreground-500">该次导出暂无可展示的批次明细（激活码池中未找到该批次）。</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length > PREVIEW_COUNT && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
            </span>
            {expanded ? "收起留痕" : `展开全部 ${filtered.length} 条`}
          </button>
        </div>
      )}
    </div>
  );
}